import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { FinancialDatas } from "src/app/domains/financial-datas";
import {
  Invoice,
  Offer,
  Project,
  ProjectMaterial,
  ProjectMilestone,
  ProjectPhase,
  ProjectPhaseTask,
  ProjectStatus,
} from "src/app/domains/internal";
import { ObjectName } from "src/app/domains/object-name";
import { RenewProject } from "src/app/domains/renew_project";

import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { environment } from "../../../../environments/environment";
import { ExpenseReport } from "../../expense-report/domains/expense-report";
import { PurchaseOrder } from "../../purchase-order/domains/purchase-order";
import { ProjectInvoiceReceivedDto } from "../dtos/project-invoice-received-dto";

/** Message affiché quand la correction du numéro de chantier échoue. */
export function corporateIdErrorMessage(err: { error_type?: string } | null | undefined): string {
  switch (err?.error_type) {
    case "PROJECT_CORPORATE_ID_ALREADY_USED":
      return "Ce numéro est déjà utilisé par un autre chantier, veuillez en choisir un autre";
    case "PROJECT_CORPORATE_ID_REQUIRED":
      return "Le numéro du chantier est obligatoire";
    default:
      return "Une erreur est survenue lors de la mise à jour du numéro du chantier";
  }
}

@Injectable({
  providedIn: "root",
})
export class ProjectService {
  url = environment.apiUrl + "/projects";
  urlRenew = environment.apiUrl + "/renew_projects";
  urlPlanning = environment.apiUrl + "/project-planning";

  constructor(private http: HttpClient) {}

  getAllProjects(projectStatus: ProjectStatus = null, viewType: string = null): Observable<Project[]> {
    let params = new HttpParams();

    if (projectStatus) {
      params = params.set("status", projectStatus);
    }
    if (viewType) {
      params = params.set("viewType", viewType);
    }
    return this.http.get<Project[]>(this.url, { params }).pipe(map(Project.createInstances));
  }

  getAllProjectsWithCoveragePercent(
    projectStatus: ProjectStatus = null,
    viewType: string = null,
  ): Observable<Project[]> {
    let params = new HttpParams();
    let url = this.url + "/with-coverage-percent";

    if (projectStatus) {
      params = params.set("status", projectStatus);
    }
    if (viewType) {
      params = params.set("viewType", viewType);
    }
    return this.http.get<Project[]>(url, { params }).pipe(map(Project.createInstances));
  }

  getAllProjectNames(projectStatus: ProjectStatus = null): Observable<ObjectName[]> {
    let url = this.url + "/names";
    if (projectStatus) {
      url += `?status=${projectStatus}`;
    }

    return this.http.get<ObjectName[]>(url).pipe(map(ObjectName.createInstances));
  }
  getProjectsFromIds(projectIds: string[]): Observable<Project[]> {
    return this.http.post<Project[]>(this.url + "/from-ids", projectIds).pipe(map(Project.createInstances));
  }

  getAllUser(): Observable<Project[]> {
    return this.http.get<Project[]>(this.url + "/me").pipe(map(Project.createInstances));
  }

  getAllProjectsFull(projectStatus: ProjectStatus = null, viewType: string = null): Observable<Project[]> {
    let params = new HttpParams();
    let url = this.url + "/full";

    if (projectStatus) {
      params = params.set("status", projectStatus);
    }
    if (viewType) {
      params = params.set("viewType", viewType);
    }

    return this.http.get<Project[]>(url, { params }).pipe(map(Project.createInstances));
  }

  getAllInputTimeProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(this.url + "/input-time").pipe(map(Project.createInstances));
  }

  getOnGoingProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(this.url + "/on-going").pipe(map(Project.createInstances));
  }

  getOnGoingProjectsLight(): Observable<Project[]> {
    return this.http
      .get<Project[]>(this.url + "/on-going", { params: { display_type: "light" } })
      .pipe(map(Project.createInstances));
  }

  getAllInterventionsProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(this.url + "/all-allow-interventions").pipe(map(Project.createInstances));
  }

  getOnGoingInterventionsProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(this.url + "/on-going-allow-interventions").pipe(map(Project.createInstances));
  }

  getProject(id, external_data = false): Observable<Project> {
    let url = this.url + "/" + id;
    if (external_data) {
      url += `?external_data=${external_data}`;
    }
    return this.http.get<Project>(url).pipe(map(Project.createInstance));
  }

  getProjectIncomes(id: string): Observable<FinancialDatas> {
    let url = this.url + "/" + id + "/incomes";
    return this.http.get<FinancialDatas>(url).pipe(map(FinancialDatas.createInstance));
  }
  getProjectExpenses(id: string): Observable<FinancialDatas> {
    let url = this.url + "/" + id + "/expenses";
    return this.http.get<FinancialDatas>(url).pipe(map(FinancialDatas.createInstance));
  }

  getProjectActivities(id): Observable<any> {
    return this.http.get<any>(this.url + "/" + id + "/week-activities").pipe(map(Project.createInstance));
  }

  addProject(project: Project): Observable<Project> {
    return this.http.post<Project>(this.url, project).pipe(map(Project.createInstance));
  }

  editProject(project: Project): Observable<Project> {
    return this.http.put<Project>(this.url + "/" + project.project_id, project).pipe(map(Project.createInstance));
  }
  editProjectDates(project: Project) {
    return this.http.put(this.url + "/" + project.project_id + "/dates", project);
  }

  deleteProject(projectId: string): Observable<any> {
    return this.http.delete(this.url + "/" + projectId);
  }

  // Materials
  addMaterial(projectId: string, material: ProjectMaterial): Observable<ProjectMaterial> {
    return this.http
      .post<ProjectMaterial>(this.url + "/" + projectId + "/materials", material)
      .pipe(map(ProjectMaterial.createInstance));
  }

  updateMaterial(projectId: string, material: ProjectMaterial): Observable<ProjectMaterial> {
    return this.http
      .put<ProjectMaterial>(this.url + "/" + projectId + "/materials/" + material.material_id, material)
      .pipe(map(ProjectMaterial.createInstance));
  }

  deleteMaterial(projectId: string, materialId: string): Observable<any> {
    return this.http.delete(this.url + "/" + projectId + "/materials/" + materialId);
  }

  //Renew Project

  getAllRenewProjects(): Observable<RenewProject[]> {
    return this.http.get<RenewProject[]>(this.urlRenew).pipe(map(RenewProject.createInstances));
  }

  getAllRenewProjectsFull(): Observable<RenewProject[]> {
    return this.http.get<RenewProject[]>(this.urlRenew + "/full").pipe(map(RenewProject.createInstances));
  }

  getRenewProject(id): Observable<RenewProject> {
    return this.http.get<RenewProject>(this.urlRenew + "/" + id).pipe(map(RenewProject.createInstance));
  }

  getRenewProjectFull(id): Observable<RenewProject> {
    return this.http.get<RenewProject>(this.urlRenew + "/" + id + "/full").pipe(map(RenewProject.createInstance));
  }

  addRenewProject(renewProject: RenewProject): Observable<RenewProject> {
    return this.http.post<RenewProject>(this.urlRenew, renewProject).pipe(map(RenewProject.createInstance));
  }

  addYearToRenewProject(renew_project_id, year): Observable<RenewProject> {
    return this.http
      .post<RenewProject>(this.urlRenew + "/" + renew_project_id + "/add-year/" + year, {})
      .pipe(map(RenewProject.createInstance));
  }

  addRenewProjectWithProject(name, project_id, year): Observable<RenewProject> {
    return this.http
      .post<RenewProject>(this.urlRenew + "/add-project", {
        name: name,
        project_id: project_id,
        year: year,
      })
      .pipe(map(RenewProject.createInstance));
  }

  renameRenewProject(id: string, name: string): Observable<RenewProject> {
    return this.http.put<RenewProject>(this.urlRenew + "/" + id, { name }).pipe(map(RenewProject.createInstance));
  }

  addProjectToRenewProject(renew_project_id, project_id, year): Observable<RenewProject> {
    return this.http
      .post<RenewProject>(this.urlRenew + "/" + renew_project_id + "/add-project", {
        project_id: project_id,
        year: year,
      })
      .pipe(map(RenewProject.createInstance));
  }

  editStatuses(projectIds: string[], selectedStatuseEdit: ProjectStatus) {
    return this.http.post<Project>(this.url + "/edit-statuses", {
      project_ids: projectIds,
      status: selectedStatuseEdit,
    });
  }

  updateProjectPartial(
    projectId: string,
    data: Partial<{ tags_ids: string[]; project_managers_id: string[] }>,
  ): Observable<Project> {
    return this.http.patch<Project>(this.url + "/" + projectId, data);
  }

  /** Corrige le seul numéro du chantier (409 PROJECT_CORPORATE_ID_ALREADY_USED si déjà pris). */
  updateCorporateId(projectId: string, projectCorporateId: string): Observable<{ project_corporate_id: string }> {
    return this.http.put<{ project_corporate_id: string }>(this.url + "/" + projectId + "/corporate-id", {
      project_corporate_id: projectCorporateId,
    });
  }

  updateComment(projectId: string, comment: string): Observable<any> {
    return this.http.put(this.url + "/" + projectId + "/comment", { comment });
  }

  cancelProject(projectId: string) {
    return this.http.post<Project>(this.url + "/" + projectId + "/cancel", {}).pipe(map(Project.createInstance));
  }

  getProjectInvoicesReceived(projectId: string): Observable<ProjectInvoiceReceivedDto[]> {
    return this.http.get<ProjectInvoiceReceivedDto[]>(this.url + "/" + projectId + "/invoices-received");
  }

  getProjectInvoicesReceivedSuppliers(
    projectId: string,
  ): Observable<{ suppliers: { supplier_id: string; name: string }[]; has_invoice_without_supplier: boolean }> {
    return this.http.get<{ suppliers: { supplier_id: string; name: string }[]; has_invoice_without_supplier: boolean }>(
      this.url + "/" + projectId + "/invoices-received/suppliers",
    );
  }

  getProjectAllExpenses(projectId: string): Observable<FinancialDatas> {
    return this.http
      .get<FinancialDatas>(this.url + "/" + projectId + "/all-expenses")
      .pipe(map(FinancialDatas.createInstance));
  }

  exportInvoicesReceivedZip(projectId: string, supplierIds?: string[]): Observable<Blob> {
    let params = new HttpParams();
    if (supplierIds && supplierIds.length > 0) {
      params = params.set("supplier_ids", supplierIds.join(","));
    }
    return this.http.get(this.url + "/" + projectId + "/invoices-received/export-zip", {
      responseType: "blob",
      params,
    });
  }

  // SPECIFIC PROJECT PLANNING

  addProjectPhase(project: Project, phase: ProjectPhase): Observable<ProjectPhase> {
    return this.http
      .post<ProjectPhase>(this.urlPlanning + "/" + project.project_id + "/phase", phase)
      .pipe(map(ProjectPhase.createInstance));
  }

  addProjectPhaseTask(project: Project, phase: ProjectPhase, task: ProjectPhaseTask): Observable<ProjectPhase> {
    return this.http
      .post<ProjectPhase>(this.urlPlanning + "/" + project.project_id + "/phase/" + phase.phase_id + "/task", task)
      .pipe(map(ProjectPhase.createInstance));
  }

  editProjectPhaseTask(project: Project, phase: ProjectPhase, task: ProjectPhaseTask): Observable<ProjectPhase> {
    return this.http
      .put<ProjectPhase>(
        this.urlPlanning + "/" + project.project_id + "/phase/" + phase.phase_id + "/task/" + task.task_id,
        task,
      )
      .pipe(map(ProjectPhase.createInstance));
  }
  editProjectPhase(project: Project, phase: ProjectPhase): Observable<ProjectPhase> {
    return this.http
      .put<ProjectPhase>(this.urlPlanning + "/" + project.project_id + "/phase/" + phase.phase_id, phase)
      .pipe(map(ProjectPhase.createInstance));
  }

  updateProjectPhases(project: Project): Observable<ProjectPhase[]> {
    let phases = project.phases;
    return this.http
      .put<ProjectPhase[]>(this.urlPlanning + "/" + project.project_id + "/phases", { phases })
      .pipe(map(ProjectPhase.createInstances));
  }

  addProjectMilestone(project: Project, milestone: ProjectMilestone): Observable<ProjectMilestone> {
    return this.http
      .post<ProjectMilestone>(this.urlPlanning + "/" + project.project_id + "/milestone", milestone)
      .pipe(map(ProjectMilestone.createInstance));
  }

  editProjectMilestone(project: Project, milestone: ProjectMilestone): Observable<ProjectMilestone> {
    return this.http
      .put<ProjectMilestone>(
        this.urlPlanning + "/" + project.project_id + "/milestone/" + milestone.milestone_id,
        milestone,
      )
      .pipe(map(ProjectMilestone.createInstance));
  }

  deleteProjectMilestone(project: Project, milestone: ProjectMilestone) {
    return this.http.delete(this.urlPlanning + "/" + project.project_id + "/milestone/" + milestone.milestone_id);
  }

  // Construction Tracking
  getConstructionTracking(projectId: string): Observable<any> {
    return this.http.get<any>(this.urlPlanning + "/" + projectId + "/construction-tracking");
  }

  importConstructionTemplate(projectId: string, templateId: string): Observable<any> {
    return this.http.post<any>(this.urlPlanning + "/" + projectId + "/import-template", {
      template_id: templateId,
    });
  }

  updateProjectPhase(projectId: string, phaseId: string, data: any): Observable<any> {
    return this.http.put<any>(this.urlPlanning + "/" + projectId + "/phase/" + phaseId, data);
  }

  deleteProjectPhase(projectId: string, phaseId: string): Observable<any> {
    return this.http.delete<any>(this.urlPlanning + "/" + projectId + "/phase/" + phaseId);
  }

  // Phase Tasks
  addPhaseTask(projectId: string, phaseId: string, taskData: any): Observable<any> {
    return this.http.post<any>(this.urlPlanning + "/" + projectId + "/phase/" + phaseId + "/task", taskData);
  }

  updatePhaseTask(projectId: string, phaseId: string, taskId: string, taskData: any): Observable<any> {
    return this.http.put<any>(this.urlPlanning + "/" + projectId + "/phase/" + phaseId + "/task/" + taskId, taskData);
  }

  deletePhaseTask(projectId: string, phaseId: string, taskId: string): Observable<any> {
    return this.http.delete<any>(this.urlPlanning + "/" + projectId + "/phase/" + phaseId + "/task/" + taskId);
  }

  getNames(status: string) {
    return this.http.get<Array<{ id: string; name: string }>>(`${this.url}/names`, {
      params: { status },
    });
  }

  // Construction Tracking Stats
  getTrackingStats(projectId: string): Observable<ConstructionTrackingStats> {
    return this.http.get<ConstructionTrackingStats>(
      this.urlPlanning + "/" + projectId + "/construction-tracking/stats",
    );
  }

  // Construction Tracking Comments
  addTrackingComment(projectId: string, comment: string, phaseId?: string, taskId?: string): Observable<any> {
    return this.http.post<any>(this.urlPlanning + "/" + projectId + "/construction-tracking/comments", {
      comment,
      phase_id: phaseId,
      task_id: taskId,
    });
  }

  getTrackingComments(projectId: string, phaseId?: string, taskId?: string): Observable<TrackingComment[]> {
    let params = new HttpParams();
    if (phaseId) params = params.set("phase_id", phaseId);
    if (taskId) params = params.set("task_id", taskId);
    return this.http.get<TrackingComment[]>(this.urlPlanning + "/" + projectId + "/construction-tracking/comments", {
      params,
    });
  }

  deleteTrackingComment(projectId: string, commentId: string): Observable<any> {
    return this.http.delete<any>(this.urlPlanning + "/" + projectId + "/tracking-comments/" + commentId);
  }

  // Milestones
  getMilestones(projectId: string): Observable<ProjectMilestone[]> {
    return this.http
      .get<ProjectMilestone[]>(this.urlPlanning + "/" + projectId + "/milestones")
      .pipe(map(ProjectMilestone.createInstances));
  }

  createMilestone(projectId: string, milestone: Partial<ProjectMilestone>): Observable<ProjectMilestone> {
    return this.http
      .post<ProjectMilestone>(this.urlPlanning + "/" + projectId + "/milestone", milestone)
      .pipe(map(ProjectMilestone.createInstance));
  }

  updateMilestone(
    projectId: string,
    milestoneId: string,
    data: Partial<ProjectMilestone>,
  ): Observable<ProjectMilestone> {
    return this.http
      .put<ProjectMilestone>(this.urlPlanning + "/" + projectId + "/milestone/" + milestoneId, data)
      .pipe(map(ProjectMilestone.createInstance));
  }

  deleteMilestone(projectId: string, milestoneId: string): Observable<any> {
    return this.http.delete(this.urlPlanning + "/" + projectId + "/milestone/" + milestoneId);
  }

  // Tracking validation
  validateTrackingWeek(projectId: string, weekNumber: number, year: number): Observable<any> {
    return this.http.post<any>(this.urlPlanning + "/" + projectId + "/tracking-validations", {
      week: weekNumber,
      year: year,
    });
  }

  getTrackingValidations(projectId: string): Observable<TrackingValidation[]> {
    return this.http.get<TrackingValidation[]>(this.urlPlanning + "/" + projectId + "/tracking-validations");
  }

  // Import template with selection
  importConstructionTemplatePartial(
    projectId: string,
    templateId: string,
    selectedPhaseIds: string[],
    selectedTaskIds: string[],
  ): Observable<any> {
    return this.http.post<any>(this.urlPlanning + "/" + projectId + "/import-template", {
      template_id: templateId,
      selected_phase_ids: selectedPhaseIds,
      selected_task_ids: selectedTaskIds,
    });
  }

  // Planning hours entry
  getTrackingPlanning(projectId: string, weekStart: Date): Observable<TrackingPlanningData> {
    const params = new HttpParams().set("week_start", weekStart.toISOString());
    return this.http.get<TrackingPlanningData>(this.urlPlanning + "/" + projectId + "/construction-tracking/planning", {
      params,
    });
  }

  updateTrackingHours(
    projectId: string,
    taskId: string,
    date: Date,
    hours: number,
    employeeId?: string,
  ): Observable<any> {
    return this.http.post<any>(this.urlPlanning + "/" + projectId + "/construction-tracking/hours", {
      task_id: taskId,
      date: date.toISOString(),
      hours: hours,
      employee_id: employeeId,
    });
  }

  // Gantt planning interactions (Phase 3.3)
  updateTaskDates(projectId: string, phaseId: string, taskId: string, startDate: Date, endDate: Date): Observable<any> {
    return this.http.put<any>(environment.apiUrl + "/tasks/" + taskId, {
      due_date: endDate.toISOString(),
      planned_start_date: startDate.toISOString(),
    });
  }

  reassignTaskToPhase(projectId: string, currentPhaseId: string, taskId: string, newPhaseId: string): Observable<any> {
    return this.http.put<any>(
      this.urlPlanning + "/" + projectId + "/phase/" + currentPhaseId + "/task/" + taskId + "/reassign",
      {
        new_phase_id: newPhaseId,
      },
    );
  }

  // Get planning data for Gantt view (extended date range)
  getGanttPlanning(projectId: string, startDate: Date, endDate: Date): Observable<GanttPlanningData> {
    const params = new HttpParams().set("start_date", startDate.toISOString()).set("end_date", endDate.toISOString());
    return this.http.get<GanttPlanningData>(this.urlPlanning + "/" + projectId + "/construction-tracking/gantt", {
      params,
    });
  }

  // ============================================
  // Phase 3.4 - Dependencies (US-130)
  // ============================================

  getDependencies(projectId: string): Observable<TaskDependency[]> {
    return this.http.get<TaskDependency[]>(this.urlPlanning + "/" + projectId + "/dependencies");
  }

  createDependency(projectId: string, data: CreateDependencyDto): Observable<TaskDependency> {
    return this.http.post<TaskDependency>(this.urlPlanning + "/" + projectId + "/dependencies", data);
  }

  deleteDependency(projectId: string, depId: string): Observable<void> {
    return this.http.delete<void>(this.urlPlanning + "/" + projectId + "/dependencies/" + depId);
  }

  // ============================================
  // Phase 3.4 - Critical Path (US-131)
  // ============================================

  getCriticalPath(projectId: string): Observable<CriticalPathData> {
    return this.http.get<CriticalPathData>(this.urlPlanning + "/" + projectId + "/critical-path");
  }

  // ============================================
  // Phase 3.4 - Tracking Views (US-132)
  // ============================================

  getTrackingViews(projectId: string): Observable<TrackingView[]> {
    return this.http.get<TrackingView[]>(this.urlPlanning + "/" + projectId + "/tracking-views");
  }

  createTrackingView(projectId: string, data: CreateViewDto): Observable<TrackingView> {
    return this.http.post<TrackingView>(this.urlPlanning + "/" + projectId + "/tracking-views", data);
  }

  updateTrackingView(projectId: string, viewId: string, data: UpdateViewDto): Observable<TrackingView> {
    return this.http.put<TrackingView>(this.urlPlanning + "/" + projectId + "/tracking-views/" + viewId, data);
  }

  deleteTrackingView(projectId: string, viewId: string): Observable<void> {
    return this.http.delete<void>(this.urlPlanning + "/" + projectId + "/tracking-views/" + viewId);
  }

  // ============================================
  // Phase 3.4 - Baselines (US-133)
  // ============================================

  getBaselines(projectId: string): Observable<TrackingBaseline[]> {
    return this.http.get<TrackingBaseline[]>(this.urlPlanning + "/" + projectId + "/baselines");
  }

  createBaseline(projectId: string, name: string): Observable<TrackingBaseline> {
    return this.http.post<TrackingBaseline>(this.urlPlanning + "/" + projectId + "/baselines", { name });
  }

  getBaselineDetail(projectId: string, baselineId: string): Observable<TrackingBaselineDetail> {
    return this.http.get<TrackingBaselineDetail>(this.urlPlanning + "/" + projectId + "/baselines/" + baselineId);
  }

  deleteBaseline(projectId: string, baselineId: string): Observable<void> {
    return this.http.delete<void>(this.urlPlanning + "/" + projectId + "/baselines/" + baselineId);
  }

  updateOngoingProjectsWorkforce(workforceHourlyCost: number, workforceGoalMargin: number): Observable<any> {
    return this.http.patch(this.url + "/update-workforce-defaults", {
      workforce_hourly_cost: workforceHourlyCost,
      workforce_goal_margin: workforceGoalMargin,
    });
  }

  updateOngoingProjectsBadgeColor(useManagerBadgeColor: boolean): Observable<any> {
    return this.http.patch(this.url + "/update-badge-color-default", {
      use_manager_badge_color: useManagerBadgeColor,
    });
  }

  getInterventionsToggleList(): Observable<ProjectInterventionToggle[]> {
    return this.http.get<ProjectInterventionToggle[]>(this.url + "/interventions-toggle-list");
  }

  bulkSetInterventions(enableIds: string[], disableIds: string[]): Observable<{ updated_count: number }> {
    return this.http.patch<{ updated_count: number }>(this.url + "/interventions-bulk", {
      enable_ids: enableIds,
      disable_ids: disableIds,
    });
  }
}

// Ligne minimale renvoyée par /projects/interventions-toggle-list (popup d'activation)
export interface ProjectInterventionToggle {
  project_id: string;
  name: string;
  project_corporate_id: string;
  allow_interventions: boolean;
}

// Interfaces for construction tracking
export interface ConstructionTrackingStats {
  hoursConsumed: number;
  hoursPlanned: number;
  daysConsumed: number;
  daysPlanned: number;
  costConsumed: number;
  costPlanned: number;
  progressPercentage: number;
  startDate: Date;
  endDate: Date;
}

export interface TrackingComment {
  comment_id: string;
  text: string;
  authorId: string;
  authorName: string;
  createdAt: Date;
  phaseId?: string;
  taskId?: string;
}

export interface TrackingValidation {
  validation_id: string;
  week: number;
  year: number;
  validatedBy: string;
  validatedAt: Date;
}

export interface TrackingPlanningData {
  phases: TrackingPlanningPhase[];
  weekStart: Date;
  weekEnd: Date;
}

export interface TrackingPlanningPhase {
  phase_id: string;
  name: string;
  color: string;
  hoursConsumed: number;
  hoursPlanned: number;
  tasks: TrackingPlanningTask[];
}

export interface TrackingPlanningTask {
  task_id: string;
  name: string;
  hoursConsumed: number;
  hoursPlanned: number;
  isCompleted: boolean;
  assignees: { employee_id: string; fullName: string }[];
  dailyHours: { [date: string]: number };
}

// Gantt planning interfaces
export interface GanttPlanningData {
  phases: GanttPlanningPhase[];
  startDate: Date;
  endDate: Date;
}

export interface GanttPlanningPhase {
  phase_id: string;
  name: string;
  color: string;
  startDate: Date;
  endDate: Date;
  tasks: GanttPlanningTask[];
}

export interface GanttPlanningTask {
  task_id: string;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  progress: number;
  assignees: { employee_id: string; fullName: string }[];
  dependencies?: string[];
}

// ============================================
// Phase 3.4 - Dependencies, Critical Path, Views, Baselines
// ============================================

// US-130: Task Dependencies
export interface TaskDependency {
  dependency_id: string;
  from_task_id: string;
  to_task_id: string;
  type: DependencyType;
  lag_days?: number;
  isCritical?: boolean;
}

export type DependencyType = "finish_to_start" | "start_to_start" | "finish_to_finish" | "start_to_finish";

export interface CreateDependencyDto {
  from_task_id: string;
  to_task_id: string;
  type: DependencyType;
  lag_days?: number;
}

// US-131: Critical Path
export interface CriticalPathData {
  critical_tasks: string[];
  total_duration_days: number;
  project_end_date: Date;
  slack_data: { [taskId: string]: number }; // slack in days per task
}

// US-132: Tracking Views
export interface TrackingView {
  view_id: string;
  name: string;
  icon: string;
  filters: TrackingViewFilters;
  sort_field?: string;
  sort_order?: "asc" | "desc";
  is_shared: boolean;
  is_default?: boolean;
  created_by: string;
  created_at: Date;
  updated_at: Date;
}

export interface TrackingViewFilters {
  assignees?: string[];
  phases?: string[];
  status?: string;
  date_range?: {
    start: Date;
    end: Date;
  };
  show_critical_only?: boolean;
  show_delayed_only?: boolean;
}

export interface CreateViewDto {
  name: string;
  icon: string;
  filters: TrackingViewFilters;
  is_shared: boolean;
}

export interface UpdateViewDto {
  name?: string;
  icon?: string;
  filters?: TrackingViewFilters;
  is_shared?: boolean;
  is_default?: boolean;
}

// US-133: Baselines
export interface TrackingBaseline {
  baseline_id: string;
  name: string;
  created_at: Date;
  created_by: string;
  snapshot_date: Date;
}

export interface TrackingBaselineDetail {
  baseline_id: string;
  name: string;
  created_at: Date;
  tasks: BaselineTask[];
}

export interface BaselineTask {
  task_id: string;
  name: string;
  start_date: Date;
  end_date: Date;
  progress: number;
  phase_id: string;
}
